/**
 * Data Vault Zustand Store — centralized state for the Data Vault page.
 *
 * Replaces 30+ useState hooks in DataVault.tsx with a single store.
 * Also absorbs CrossFilterContext and highlightedWells from terra.ts.
 */
import { create } from 'zustand';
import type { NdHandling, ConcentrationUnit } from '@/api/dataVault';

// ── Types ─────────────────────────────────────────────────────────────

export type DataSource = 'chemistry' | 'water-levels' | 'compliance' | 'qaqc';

// Which purchased product's data feeds the tabs (toggle at top of the page).
// Tabs stay the same; this swaps the underlying data domain:
//   polygon  → analyte concentrations + plume predictions
//   phreatic → water availability + phreatic predictions
export type ProductView = 'polygon' | 'phreatic';

export interface CrossFilterState {
  well?: string;
  date?: string;
  analyte?: string;
}

interface SortState {
  by: string;
  dir: 'asc' | 'desc';
}

// ── Store Interface ──────────────────────────────────────────────────

interface DataVaultStore {
  // ─── Filters ────────────────────────────────────────────────────────
  activeProduct: ProductView;
  // Global ML forecast horizon in years (0 = now). Drives every chart:
  // time-series plot the forecast forward to this year; static diagrams
  // (e.g. Sankey) render the snapshot at this year.
  horizonYears: number;
  dataSource: DataSource;
  selectedSites: string[];
  selectedWells: string[];
  selectedAnalytes: string[];
  dateFrom: string;
  dateTo: string;
  ndHandling: NdHandling;
  exceedsMclOnly: boolean;

  // ─── View ───────────────────────────────────────────────────────────
  concUnit: ConcentrationUnit;
  showAnomalies: boolean;
  showCompare: boolean;
  showAuditHistory: boolean;

  // ─── Well construction panel ────────────────────────────────────────
  selectedWellId: string | null;

  // ─── QA/QC ──────────────────────────────────────────────────────────
  qaqcExpanded: boolean;

  // ─── Cross-filtering (migrated from CrossFilterContext) ─────────────
  crossFilter: CrossFilterState;
  highlightedWells: string[];

  // ─── Per-tab pagination & sorting ───────────────────────────────────
  analyticalSort: SortState;
  analyticalPage: number;
  gaugingSort: SortState;
  gaugingPage: number;
  riskPage: number;
  costPage: number;
  plumePage: number;
  compliancePage: number;

  // ─── Regulatory line visibility ─────────────────────────────────────
  hiddenRegulatoryLibraries: Set<string>;
  /** Libraries with at least one rendered line on the currently-active chart.
   *  Written by useRegulatoryLines as the chart mounts / visibility changes.
   *  Read by FilterRibbon to decide which STANDARDS chips to show. */
  currentChartRegulatoryLibraries: { key: string; label: string; color: string }[];

  // ─── Brush sync (Sprint 4) ─────────────────────────────────────────
  brushRange: { from: string; to: string } | null;

  // ─── Filter actions ─────────────────────────────────────────────────
  setActiveProduct: (p: ProductView) => void;
  setHorizonYears: (y: number) => void;
  setDataSource: (ds: DataSource) => void;
  setSelectedSites: (ids: string[]) => void;
  setSelectedWells: (ids: string[]) => void;
  setSelectedAnalytes: (codes: string[]) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setNdHandling: (v: NdHandling) => void;
  setExceedsMclOnly: (v: boolean) => void;

  /** Set sites + reset wells and all page counters */
  changeSites: (ids: string[]) => void;
  /** Set wells + reset page counters */
  changeWells: (ids: string[]) => void;
  /** Set analytes + reset analytical page */
  changeAnalytes: (codes: string[]) => void;
  /** Set dateFrom + reset pages */
  changeDateFrom: (v: string) => void;
  /** Set dateTo + reset pages */
  changeDateTo: (v: string) => void;
  /** Set ndHandling + reset analytical page */
  changeNdHandling: (v: NdHandling) => void;
  /** Set exceedsMclOnly + reset analytical page */
  changeExceedsMclOnly: (v: boolean) => void;

  // ─── View actions ───────────────────────────────────────────────────
  setConcUnit: (u: ConcentrationUnit) => void;
  toggleAnomalies: () => void;
  toggleCompare: () => void;
  toggleAuditHistory: () => void;
  setSelectedWellId: (id: string | null) => void;
  setQaqcExpanded: (v: boolean) => void;

  // ─── Regulatory line actions ─────────────────────────────────────────
  toggleRegulatoryLibrary: (library: string) => void;
  setCurrentChartRegulatoryLibraries: (libs: { key: string; label: string; color: string }[]) => void;

  // ─── Cross-filter actions ───────────────────────────────────────────
  highlightCrossFilter: (filter: CrossFilterState) => void;
  clearCrossFilter: () => void;
  isCrossFilterActive: () => boolean;
  isHighlighted: (well?: string, date?: string, analyte?: string) => boolean;
  getOpacity: (well?: string, date?: string, analyte?: string) => number;

  // ─── Well highlight actions (merged from terra.ts) ──────────────────
  setHighlightedWells: (wells: string[]) => void;
  clearHighlights: () => void;

  // ─── Sort/page actions ──────────────────────────────────────────────
  setAnalyticalSort: (col: string) => void;
  setAnalyticalPage: (p: number) => void;
  setGaugingSort: (col: string) => void;
  setGaugingPage: (p: number) => void;
  setRiskPage: (p: number) => void;
  setCostPage: (p: number) => void;
  setPlumePage: (p: number) => void;
  setCompliancePage: (p: number) => void;

  // ─── Brush sync ─────────────────────────────────────────────────────
  setBrushRange: (range: { from: string; to: string } | null) => void;

  // ─── Bulk actions ───────────────────────────────────────────────────
  resetAllPages: () => void;
  resetAllFilters: () => void;
  loadPreset: (opts: {
    sites?: string[];
    wells?: string[];
    analytes?: string[];
    dateFrom?: string;
    dateTo?: string;
    ndHandling?: NdHandling;
    exceedsMclOnly?: boolean;
    concUnit?: ConcentrationUnit;
  }) => void;
}

// ── Helper ─────────────────────────────────────────────────────────────

const PAGE_RESET = {
  analyticalPage: 1,
  gaugingPage: 1,
  riskPage: 1,
  costPage: 1,
  plumePage: 1,
  compliancePage: 1,
} as const;

// ── Store ──────────────────────────────────────────────────────────────

export const useDataVaultStore = create<DataVaultStore>((set, get) => ({
  // Initial state
  activeProduct: 'polygon',
  horizonYears: 0,
  dataSource: 'chemistry',
  selectedSites: [],
  selectedWells: [],
  selectedAnalytes: [],
  dateFrom: '',
  dateTo: '',
  ndHandling: 'half_mdl',
  exceedsMclOnly: false,

  concUnit: 'mg/L',
  showAnomalies: false,
  showCompare: false,
  showAuditHistory: false,

  selectedWellId: null,
  qaqcExpanded: false,

  crossFilter: {},
  highlightedWells: [],

  analyticalSort: { by: 'sample_date', dir: 'desc' },
  analyticalPage: 1,
  gaugingSort: { by: 'sort_order', dir: 'asc' },
  gaugingPage: 1,
  riskPage: 1,
  costPage: 1,
  plumePage: 1,
  compliancePage: 1,

  hiddenRegulatoryLibraries: new Set<string>(),
  currentChartRegulatoryLibraries: [],
  brushRange: null,

  // ─── Filter setters (raw — no page reset) ───────────────────────────
  setActiveProduct: (p) => set({ activeProduct: p }),
  setHorizonYears: (y) => set({ horizonYears: y }),
  setDataSource: (ds) => set({ dataSource: ds }),
  setSelectedSites: (ids) => set({ selectedSites: ids }),
  setSelectedWells: (ids) => set({ selectedWells: ids }),
  setSelectedAnalytes: (codes) => set({ selectedAnalytes: codes }),
  setDateFrom: (v) => set({ dateFrom: v }),
  setDateTo: (v) => set({ dateTo: v }),
  setNdHandling: (v) => set({ ndHandling: v }),
  setExceedsMclOnly: (v) => set({ exceedsMclOnly: v }),

  // ─── Filter changers (with page reset) ──────────────────────────────
  changeSites: (ids) => set({ selectedSites: ids, selectedWells: [], ...PAGE_RESET }),
  changeWells: (ids) => set({ selectedWells: ids, analyticalPage: 1, gaugingPage: 1 }),
  changeAnalytes: (codes) => set({ selectedAnalytes: codes, analyticalPage: 1 }),
  changeDateFrom: (v) => set({ dateFrom: v, analyticalPage: 1, gaugingPage: 1 }),
  changeDateTo: (v) => set({ dateTo: v, analyticalPage: 1, gaugingPage: 1 }),
  changeNdHandling: (v) => set({ ndHandling: v, analyticalPage: 1 }),
  changeExceedsMclOnly: (v) => set({ exceedsMclOnly: v, analyticalPage: 1 }),

  // ─── View actions ───────────────────────────────────────────────────
  setConcUnit: (u) => set({ concUnit: u }),
  toggleAnomalies: () => set((s) => ({ showAnomalies: !s.showAnomalies })),
  toggleCompare: () => set((s) => ({ showCompare: !s.showCompare })),
  toggleAuditHistory: () => set((s) => ({ showAuditHistory: !s.showAuditHistory })),
  setSelectedWellId: (id) => set({ selectedWellId: id }),
  setQaqcExpanded: (v) => set({ qaqcExpanded: v }),

  // ─── Cross-filter actions ───────────────────────────────────────────
  highlightCrossFilter: (filter) => {
    set((s) => {
      const prev = s.crossFilter;
      // Toggle off if clicking same selection
      if (prev.well === filter.well && prev.date === filter.date && prev.analyte === filter.analyte) {
        return { crossFilter: {} };
      }
      return { crossFilter: filter };
    });
  },

  clearCrossFilter: () => set({ crossFilter: {} }),

  isCrossFilterActive: () => {
    const { crossFilter } = get();
    return !!(crossFilter.well || crossFilter.date || crossFilter.analyte);
  },

  isHighlighted: (well?: string, date?: string, analyte?: string) => {
    const { crossFilter } = get();
    const isActive = !!(crossFilter.well || crossFilter.date || crossFilter.analyte);
    if (!isActive) return true;
    if (crossFilter.well && well && crossFilter.well !== well) return false;
    if (crossFilter.date && date && crossFilter.date !== date) return false;
    if (crossFilter.analyte && analyte && crossFilter.analyte !== analyte) return false;
    return true;
  },

  getOpacity: (well?: string, date?: string, analyte?: string) => {
    const store = get();
    const isActive = !!(store.crossFilter.well || store.crossFilter.date || store.crossFilter.analyte);
    if (!isActive) return 1;
    return store.isHighlighted(well, date, analyte) ? 1 : 0.15;
  },

  // ─── Well highlight actions ─────────────────────────────────────────
  setHighlightedWells: (wells) => set({ highlightedWells: wells }),
  clearHighlights: () => set({ highlightedWells: [] }),

  // ─── Sort actions ───────────────────────────────────────────────────
  // Text columns default to 'asc' on first click (so MW-1 leads), numeric/date
  // columns default to 'desc' (most recent / largest first).
  setAnalyticalSort: (col) =>
    set((s) => {
      const textCols = new Set([
        'site_name', 'well_name', 'compound', 'analyte_code', 'lab_method', 'qualifier',
      ]);
      const defaultDir: 'asc' | 'desc' = textCols.has(col) ? 'asc' : 'desc';
      return {
        analyticalSort: {
          by: col,
          dir: col === s.analyticalSort.by
            ? (s.analyticalSort.dir === 'asc' ? 'desc' : 'asc')
            : defaultDir,
        },
        analyticalPage: 1,
      };
    }),

  setAnalyticalPage: (p) => set({ analyticalPage: p }),

  setGaugingSort: (col) =>
    set((s) => {
      const textCols = new Set(['site_name', 'well_name']);
      const defaultDir: 'asc' | 'desc' = textCols.has(col) ? 'asc' : 'desc';
      return {
        gaugingSort: {
          by: col,
          dir: col === s.gaugingSort.by
            ? (s.gaugingSort.dir === 'asc' ? 'desc' : 'asc')
            : defaultDir,
        },
        gaugingPage: 1,
      };
    }),

  setGaugingPage: (p) => set({ gaugingPage: p }),
  setRiskPage: (p) => set({ riskPage: p }),
  setCostPage: (p) => set({ costPage: p }),
  setPlumePage: (p) => set({ plumePage: p }),
  setCompliancePage: (p) => set({ compliancePage: p }),

  // ─── Brush sync ─────────────────────────────────────────────────────
  setBrushRange: (range) => set({ brushRange: range }),

  // ─── Regulatory line visibility ─────────────────────────────────────
  toggleRegulatoryLibrary: (library) => set((s) => {
    const next = new Set(s.hiddenRegulatoryLibraries);
    if (next.has(library)) next.delete(library);
    else next.add(library);
    return { hiddenRegulatoryLibraries: next };
  }),
  setCurrentChartRegulatoryLibraries: (libs) =>
    set({ currentChartRegulatoryLibraries: libs }),

  // ─── Bulk actions ───────────────────────────────────────────────────
  resetAllPages: () => set(PAGE_RESET),

  resetAllFilters: () =>
    set({
      selectedSites: [],
      selectedWells: [],
      selectedAnalytes: [],
      dateFrom: '',
      dateTo: '',
      ndHandling: 'half_mdl',
      exceedsMclOnly: false,
      crossFilter: {},
      highlightedWells: [],
      hiddenRegulatoryLibraries: new Set<string>(),
      brushRange: null,
      ...PAGE_RESET,
    }),

  loadPreset: (opts) =>
    set({
      selectedSites: opts.sites ?? [],
      selectedWells: opts.wells ?? [],
      selectedAnalytes: opts.analytes ?? [],
      dateFrom: opts.dateFrom ?? '',
      dateTo: opts.dateTo ?? '',
      ndHandling: opts.ndHandling ?? 'half_mdl',
      exceedsMclOnly: opts.exceedsMclOnly ?? false,
      concUnit: opts.concUnit ?? get().concUnit,
      ...PAGE_RESET,
    }),
}));
